import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet, FieldTitle } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TarjetasRadio } from '@/components/comunes/tarjetas-radio';
import { ApiError } from '@/lib/query-client';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import { EstadoChip } from '@/components/rdt/estado-chip';
import { entregaElegida, esFechaFutura, hitoAplicable, motivoEntregaBloqueada, normalizarNro } from '@/components/rdt/rdt-domain';
import {
  useActualizarRdt,
  useCrearRdt,
  type ActualizarRdtHitosInput,
  type ActualizarRdtInput,
  type CrearRdtInput,
} from '@/hooks/use-rdt-mutations';
import { useMe } from '@/hooks/use-me';
import { useUsuarios } from '@/hooks/use-usuarios';
import type { ObraDetalle, RdtDetalle, TipoRdt } from '@/types/api';

type EntregaCodigo = 'entregado_obra' | 'entregado_cliente';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Fecha más tardía entre 0..N strings ISO 'YYYY-MM-DD' (comparación lexical,
 *  válida para este formato) — los vacíos se ignoran. Se usa para el `min` de
 *  Facturado, que tiene que ser posterior a AMBOS Aprobado y Póliza EC (si
 *  aplica) — el bug reportado (punto 18) era que el min ignoraba Póliza. */
function fechaMasTardia(...fechas: string[]): string | undefined {
  const validas = fechas.filter(Boolean);
  if (validas.length === 0) return undefined;
  return validas.reduce((max, f) => (f > max ? f : max));
}

interface HitoCandidato {
  nombre: string;
  fecha: string;
}

/** Espejo cliente de validarCronologia (backend/src/domain/rdt-estado.ts):
 *  mismo criterio (fecha no decreciente en el orden canónico del ciclo:
 *  entrega → aprobado → [póliza] → facturado), pero corriendo ANTES del
 *  guardado sobre el estado del form — el 422 del backend aplica la MISMA
 *  regla pero sin decir qué par la viola ni que se puede corregir todo junto
 *  acá mismo, en un solo guardado (punto 18). */
function validarCronologiaForm(candidatos: HitoCandidato[]): { anterior: HitoCandidato; posterior: HitoCandidato } | null {
  const conFecha = candidatos.filter(h => h.fecha);
  for (let i = 1; i < conFecha.length; i++) {
    if (conFecha[i]!.fecha < conFecha[i - 1]!.fecha) {
      return { anterior: conFecha[i - 1]!, posterior: conFecha[i]! };
    }
  }
  return null;
}

interface Props {
  obra: ObraDetalle;
  /** null = alta ("Nueva"). */
  rdt: RdtDetalle | null;
  onCancelar: () => void;
  onGuardado: (nuevoId?: number) => void;
}

// "Todo es por actividad" (Juan 2026-07-11): la identidad (nro/responsable)
// de una RDT existente ahora se edita INLINE en la ficha (ficha.tsx) y
// referencia/%/monto entran por pasos del hilo — este componente quedó
// reducido a DOS casos bien angostos:
//   - Alta ("Nueva"): SOLO nro + tipo + responsable (alta mínima).
//   - Edición ("Corregir hitos"): SOLO la sección de hitos (corrección de
//     fechas, entrega XOR, toggle póliza) — el PUT de hitos sigue igual.
export function EdicionRdt({ obra, rdt, onCancelar, onGuardado }: Props) {
  const isNew = rdt === null;
  const { data: me } = useMe();
  const { data: usuarios } = useUsuarios();
  const crearRdt = useCrearRdt();
  const actualizarRdt = useActualizarRdt();
  const guardando = crearRdt.isPending || actualizarRdt.isPending;

  // Snapshot de los hitos ORIGINALES al ABRIR el form — useState con
  // initializer, se evalúa UNA sola vez al montar y nunca se re-sincroniza
  // si `rdt` cambia de referencia mientras el form sigue abierto (un
  // refetch de fondo — ej. otra persona completando un hito en simultáneo —
  // no debe hacer que el diff de handleGuardar detecte "cambios" en hitos
  // que ESTE form nunca tocó y se los mande de vuelta viejos, revirtiendo el
  // cambio ajeno). Antes esto eran `const` recalculadas en cada render a
  // partir de `rdt` — el campo editable (fechaAprobado, etc., más abajo) sí
  // estaba bien (useState solo usa su valor inicial una vez), pero el
  // "original" contra el que se diffea en handleGuardar NO lo estaba.
  // EdicionRdt igual remonta entera si cambia el id de la RDT editada (la
  // `key` que le pone obra-detalle.tsx), así que esto solo protege el caso
  // "mismo id, props actualizadas".
  const [original] = useState(() => ({
    aprobado: rdt ? hitoAplicable(rdt.hitos, 'aprobado') : null,
    poliza: rdt ? hitoAplicable(rdt.hitos, 'poliza_ec_enviada') : null,
    facturado: rdt ? hitoAplicable(rdt.hitos, 'facturado') : null,
    entrega: rdt ? entregaElegida(rdt.hitos) : null,
  }));
  const aprobadoOriginal = original.aprobado;
  const polizaOriginal = original.poliza;
  const facturadoOriginal = original.facturado;
  const entregaOriginal = original.entrega;
  // Guarda: la entrega (decisión excluyente) no se cambia si ya hay hitos
  // posteriores completados — habría que deshacerlos primero (mismo criterio backend).
  const posteriores = Boolean(aprobadoOriginal?.fecha || polizaOriginal?.fecha || facturadoOriginal?.fecha);
  // R1 (Juan, 2026-09-14): mismo criterio que paso-forms.tsx/HitoForm — si
  // esta rede TODAVÍA no tiene ninguna rama de entrega elegida (`entrega`
  // era null al abrir el form) y el backend bloquearía el alta (falta el
  // salto/el monto), los radios se deshabilitan acá también, así no rebota
  // en un 422 recién al guardar. Si YA hay una rama elegida (típico de una
  // migrada Aprobada/Facturada backfilleada antes de esta guarda, o de una
  // edición retomada) no aplica: R1 solo protege AGREGAR una entrega nueva
  // (bloque «Entrega (XOR)» del PUT), no corregir la fecha de una ya cargada.
  const motivoEntrega = rdt && entregaOriginal === null ? motivoEntregaBloqueada(rdt) : null;

  // Alta mínima: nro + tipo + responsable (default: usuario logueado).
  const [nro, setNro] = useState('');
  const [tipo, setTipo] = useState<TipoRdt>('Provisoria');
  const [responsableId, setResponsableId] = useState<number | null>(me?.id ?? null);

  // Hitos (solo edición).
  const [entrega, setEntrega] = useState<EntregaCodigo | null>((entregaOriginal?.codigo as EntregaCodigo | undefined) ?? null);
  // R3 (misma forma que «+ Paso»): UNA sola fecha para la entrega, con
  // independencia de la rama elegida — antes eran dos estados
  // (fechaEntregaObra/fechaEntregaCliente) espejando un Input date por
  // opción; con TarjetasRadio la fecha vale para la rama que esté elegida.
  const [fechaEntrega, setFechaEntrega] = useState(entregaOriginal?.fecha?.slice(0, 10) ?? '');
  const [fechaAprobado, setFechaAprobado] = useState(aprobadoOriginal?.fecha?.slice(0, 10) ?? '');
  const [polizaAplica, setPolizaAplica] = useState(polizaOriginal !== null);
  const [fechaPoliza, setFechaPoliza] = useState(polizaOriginal?.fecha?.slice(0, 10) ?? '');
  const [fechaFacturado, setFechaFacturado] = useState(facturadoOriginal?.fecha?.slice(0, 10) ?? '');
  // Nota (Juan, validación v0.12.0, 2026-09-15: «la nota es del acto, no del
  // dato»): solo tiene sentido en modo edición («Corregir hitos» sobre una
  // RDT ya existente) — el estado sigue llamándose `motivo` (así viaja al
  // PUT) aunque el label visible sea «Nota». SIEMPRE arranca vacío, nunca
  // como parte de un hito — es el motivo del acto de corrección, no un dato
  // del hito en sí.
  const [motivo, setMotivo] = useState('');

  const entregaFechaActual = entrega ? fechaEntrega : '';
  const aprobadoHabilitado = Boolean(fechaAprobado || entregaFechaActual);
  const facturadoHabilitado = Boolean(fechaFacturado || fechaAprobado);

  function handleGuardar(): void {
    if (isNew) {
      const nroTrim = nro.trim();
      if (!nroTrim) return;
      const input: CrearRdtInput = { cc: obra.cc, nro: nroTrim, tipo, responsableId };
      crearRdt.mutate(input, {
        onSuccess: data => {
          // Decisión de Juan 2026-09-14 (ver rdt-domain.ts normalizarNro):
          // el toast muestra el nro NORMALIZADO que quedó guardado, no
          // necesariamente lo que se tipeó — usa el de la respuesta si vino,
          // si no lo normaliza acá mismo.
          toast.success(`Rede #${data.nro ?? normalizarNro(nroTrim)} creada`);
          onGuardado(data.id);
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo crear la redeterminación')),
      });
      return;
    }

    // Validación cruzada client-side (punto 18) ANTES de armar el patch —
    // espejo de validarCronologia (backend): si el form dejara una fecha
    // posterior antes que una anterior, se corta acá con un mensaje que
    // nombra el par y aclara que se puede corregir todo junto en un
    // guardado, en vez de que el 422 del backend llegue después sin
    // contexto (hoy "parece un no se puede").
    const candidatos: HitoCandidato[] = [
      ...(entrega
        ? [{ nombre: entrega === 'entregado_obra' ? 'Entregado a obra' : 'Entregado al cliente', fecha: entregaFechaActual }]
        : []),
      { nombre: 'Aprobado', fecha: fechaAprobado },
      ...(polizaAplica ? [{ nombre: 'Póliza EC enviada al cliente', fecha: fechaPoliza }] : []),
      { nombre: 'Facturado', fecha: fechaFacturado },
    ];
    const violacion = validarCronologiaForm(candidatos);
    if (violacion) {
      toast.error(
        `La fecha de "${violacion.posterior.nombre}" no puede ser anterior a "${violacion.anterior.nombre}" — se puede corregir todo junto acá mismo, en un solo guardado.`,
      );
      return;
    }

    // El PUT es un PATCH semántico (backend/src/routes/rdts.ts decide qué
    // toca por hasOwnProperty): mandar SOLO los hitos que cambiaron — sin
    // esto, una migrada con Aprobado/Facturado histórico y SIN entrega no
    // debe re-mandar esas fechas si nadie las tocó (el backend lo trataría
    // como "completar" un hito posterior sin entrega elegida y rechaza con
    // 422, tirando abajo todo el guardado).
    const entregaCodigoOriginal = (entregaOriginal?.codigo as EntregaCodigo | undefined) ?? null;
    const polizaAplicaOriginal = polizaOriginal !== null;
    const fechaAprobadoOriginal = aprobadoOriginal?.fecha?.slice(0, 10) ?? '';
    const fechaFacturadoOriginal = facturadoOriginal?.fecha?.slice(0, 10) ?? '';
    const fechaEntregaOriginal = entregaOriginal?.codigo === entrega ? (entregaOriginal.fecha?.slice(0, 10) ?? '') : '';
    const fechaPolizaOriginal = polizaOriginal?.fecha?.slice(0, 10) ?? '';

    const fechas: Record<string, string | null> = {};
    if (fechaAprobado !== fechaAprobadoOriginal) fechas.aprobado = fechaAprobado || null;
    if (fechaFacturado !== fechaFacturadoOriginal) fechas.facturado = fechaFacturado || null;
    if (entrega && entregaFechaActual !== fechaEntregaOriginal) fechas[entrega] = entregaFechaActual || null;
    if (polizaAplica && fechaPoliza !== fechaPolizaOriginal) fechas.poliza_ec_enviada = fechaPoliza || null;

    const hitos: ActualizarRdtHitosInput = {
      ...(entrega !== entregaCodigoOriginal ? { entrega } : {}),
      ...(polizaAplica !== polizaAplicaOriginal ? { polizaAplica } : {}),
      ...(Object.keys(fechas).length > 0 ? { fechas } : {}),
    };

    if (Object.keys(hitos).length === 0) {
      // Review adversarial v0.12.0: si la gestora escribió una nota pero no
      // tocó ningún hito, antes el panel se cerraba «como si hubiera
      // guardado» y el texto se perdía en silencio. La nota acompaña una
      // corrección — sin corrección no hay eco que la lleve: se avisa y el
      // form queda abierto para que decida.
      if (motivo.trim()) {
        toast.error('No hay cambios en los hitos para guardar — la nota acompaña una corrección de fecha, entrega o póliza.');
        return;
      }
      onGuardado();
      return;
    }

    const patch: ActualizarRdtInput = {
      cc: obra.cc,
      id: rdt!.id,
      hitos,
      // Motivo (QA ronda 2): solo viaja si se escribió algo — nunca se manda
      // `motivo: null`.
      ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
    };
    actualizarRdt.mutate(patch, {
      onSuccess: () => {
        toast.success('Cambios guardados');
        onGuardado();
      },
      onError: err => toast.error(errorMessage(err, 'No se pudo guardar')),
    });
  }

  // Aprobado/Facturado (hitos "fijos"): el Input queda igual habilitado o
  // deshabilitado, la única diferencia es el Tooltip que explica por qué —
  // se arma una vez acá para no repetir el <Input> completo en las dos ramas
  // del ternario (§1.11: sin `title` nativo).
  const inputFechaAprobadoBase = (
    <Input
      type="date"
      aria-label="Fecha de Aprobado"
      value={fechaAprobado}
      min={entregaFechaActual || undefined}
      // Opcional (punto 18): tope superior para que el rango legal se vea
      // reflejado en las 4 fechas — Aprobado no puede ser posterior a
      // Póliza (si aplica) ni a Facturado.
      max={polizaAplica && fechaPoliza ? fechaPoliza : fechaFacturado || undefined}
      disabled={!aprobadoHabilitado}
      onChange={e => setFechaAprobado(e.target.value)}
      className="h-8 w-[150px] flex-none text-xs"
    />
  );
  const inputFechaAprobado = aprobadoHabilitado ? (
    inputFechaAprobadoBase
  ) : (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex flex-none" />}>{inputFechaAprobadoBase}</TooltipTrigger>
      <TooltipContent>Se habilita al completar el hito anterior</TooltipContent>
    </Tooltip>
  );

  const inputFechaFacturadoBase = (
    <Input
      type="date"
      aria-label="Fecha de Facturado"
      value={fechaFacturado}
      // Bug punto 18: el min ignoraba Póliza — tiene que ser posterior a
      // AMBOS Aprobado y Póliza EC (si aplica), no solo Aprobado.
      min={fechaMasTardia(fechaAprobado, polizaAplica ? fechaPoliza : '')}
      disabled={!facturadoHabilitado}
      onChange={e => setFechaFacturado(e.target.value)}
      className="h-8 w-[150px] flex-none text-xs"
    />
  );
  const inputFechaFacturado = facturadoHabilitado ? (
    inputFechaFacturadoBase
  ) : (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex flex-none" />}>{inputFechaFacturadoBase}</TooltipTrigger>
      <TooltipContent>Se habilita al completar el hito anterior</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="rounded-[11px] border border-primary/45 bg-surface-card shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_9%,transparent)]">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-border-subtle px-[18px] pb-[13px] pt-[15px]">
        <h2 className="m-0 text-base font-[650] tracking-[-0.01em]">
          {isNew ? (
            'Nueva redeterminación'
          ) : (
            <>
              <span className="font-mono">Rede #{rdt.nro}</span> <span className="text-xs font-medium text-muted-foreground">Corregir hitos</span>
            </>
          )}
        </h2>
        <div className="ml-auto">
          <EstadoChip estado={isNew ? (tipo === 'Definitiva' ? 'A calcular' : 'A chequear') : rdt.estado} />
        </div>
      </div>

      {isNew ? (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-[22px] gap-y-3.5 px-[18px] py-[15px]">
          <Field>
            <FieldLabel htmlFor="ed-nro" className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Rede Nº *
            </FieldLabel>
            <Input id="ed-nro" value={nro} onChange={e => setNro(e.target.value)} placeholder="Ej: 16, 1 a 15 Adic 1" autoComplete="off" />
          </Field>

          <Field>
            <FieldLabel htmlFor="ed-tipo" className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Tipo
            </FieldLabel>
            <Select value={tipo} onValueChange={v => v && setTipo(v as TipoRdt)}>
              <SelectTrigger id="ed-tipo" className="w-full">
                <SelectValue>{(v: string) => v}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Provisoria">Provisoria</SelectItem>
                <SelectItem value="Definitiva">Definitiva</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel id="ed-responsable-label" className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Responsable
            </FieldLabel>
            <Select
              value={responsableId != null ? String(responsableId) : 'sin-asignar'}
              onValueChange={v => setResponsableId(v === 'sin-asignar' ? null : Number(v))}
            >
              <SelectTrigger aria-labelledby="ed-responsable-label" className="w-full">
                <SelectValue>
                  {(v: string) => {
                    if (!v || v === 'sin-asignar') return 'Sin asignar';
                    const u = usuarios?.find(u2 => String(u2.id) === v);
                    if (!u) return '…';
                    return (
                      <span className="flex items-center gap-2">
                        <AvatarPersona nombre={u.nombre} fotoUrl={u.fotoUrl} size={18} />
                        {u.nombre}
                      </span>
                    );
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sin-asignar">Sin asignar</SelectItem>
                {(usuarios ?? []).map(u => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    <span className="flex items-center gap-2">
                      <AvatarPersona nombre={u.nombre} fotoUrl={u.fotoUrl} size={18} />
                      {u.nombre}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <FieldDescription className="[grid-column:1/-1]">
            Referencia, porcentaje/monto y el resto de la gestión se cargan después, paso a paso, desde Actividad.
          </FieldDescription>
        </div>
      ) : (
        <div className="px-[18px] pb-1 pt-3">
          {/* Corregir hitos: recortado y reorganizado para paridad con la rc.1
              (rc.6, qa_visual_reui_2026-09-28.md U-5/U-6) — el diálogo entra
              completo en 1440×694 y 1024×768, con Cancelar/Guardar visibles y
              sin scroll, con las tarjetas de radio (TarjetasRadio) ya
              aprobadas. Las 4 filas de hitos no fijos (Entregada a, Fecha de
              entrega, Requiere póliza EC, Fecha de póliza EC) usan Field
              orientation="horizontal": en vertical, Field le mete `*:w-full` a
              todos sus hijos (estira el date input de 150px a ~600px) y suma
              una línea entera de label por fila. Los labels de TarjetasRadio
              (Entregada a / Requiere póliza EC) llevan whitespace-nowrap para
              no partirse en 2 líneas sin competirle el shrink al RadioGroup,
              que ya trae w-full de fábrica y absorbe el ancho sobrante. El
              FieldDescription condicional de cada fila sale del Field a un div
              hermano — dentro de un Field horizontal quedaría centrado en la
              fila en vez de debajo. */}
          <FieldSet className="gap-3">
            <FieldLegend variant="label">Hitos del ciclo (la entrega se elige: obra o cliente, nunca las dos)</FieldLegend>
            <FieldGroup className="gap-1.5">
              {/* R3 (misma forma que «+ Paso»): TarjetasRadio para elegir la
                  rama de entrega, con UNA sola fecha debajo — antes era un
                  Input date por opción (fechaEntregaObra/fechaEntregaCliente). */}
              <div className="flex flex-col gap-1.5">
                <Field orientation="horizontal">
                  <FieldLabel id="ed-entrega-label" className="whitespace-nowrap">
                    Entregada a
                  </FieldLabel>
                  <TarjetasRadio
                    valor={entrega}
                    onValorChange={v => setEntrega(v)}
                    opciones={[
                      { valor: 'entregado_obra', titulo: 'Entregado a obra' },
                      { valor: 'entregado_cliente', titulo: 'Entregado al cliente' },
                    ]}
                    columnas={2}
                    // Destraba el backfill de entrega en migradas facturadas
                    // (punto 18): si `entrega` todavía es null (nunca se eligió
                    // rama) las tarjetas quedan habilitadas aunque ya haya hitos
                    // posteriores completados — el backend ya lo permite desde
                    // v0.7.0, la UI lo re-bloqueaba de más.
                    // R1: y al revés, si falta el salto/el monto, ninguna rama
                    // nueva se puede elegir todavía (motivoEntrega más abajo).
                    disabled={(posteriores && entregaOriginal !== null) || motivoEntrega !== null}
                    aria-labelledby="ed-entrega-label"
                  />
                </Field>
                {/* R1 (Juan, 2026-09-14): mismo texto que el 422 del backend —
                    mismo helper que HitoForm (paso-forms.tsx). */}
                {motivoEntrega && <FieldDescription className="text-warning">{motivoEntrega}</FieldDescription>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="ed-fecha-entrega">Fecha de entrega</FieldLabel>
                  <Input
                    id="ed-fecha-entrega"
                    type="date"
                    value={fechaEntrega}
                    disabled={entrega === null}
                    onChange={e => setFechaEntrega(e.target.value)}
                    className="h-8 w-[150px] flex-none text-xs"
                  />
                </Field>
                {esFechaFutura(fechaEntrega) && <FieldDescription className="text-warning">La fecha es futura</FieldDescription>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Field orientation="horizontal" className="rounded-[9px] border border-border-subtle bg-surface-page px-2.5 py-1">
                  <Checkbox defaultChecked disabled aria-label="Aprobado — hito fijo" />
                  <FieldContent>
                    <FieldTitle>
                      Aprobado <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">· fijo</span>
                    </FieldTitle>
                  </FieldContent>
                  {inputFechaAprobado}
                </Field>
                {esFechaFutura(fechaAprobado) && <FieldDescription className="pl-2.5 text-warning">La fecha es futura</FieldDescription>}
              </div>

              <Field orientation="horizontal">
                <FieldLabel id="ed-poliza-label" className="whitespace-nowrap">
                  Requiere póliza EC
                </FieldLabel>
                <TarjetasRadio
                  valor={polizaAplica ? 'si' : 'no'}
                  onValorChange={v => setPolizaAplica(v === 'si')}
                  opciones={[
                    { valor: 'no', titulo: 'No' },
                    { valor: 'si', titulo: 'Sí' },
                  ]}
                  columnas={2}
                  disabled={Boolean(polizaOriginal?.fecha)}
                  aria-labelledby="ed-poliza-label"
                />
              </Field>

              <div className="flex flex-col gap-1.5">
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="ed-fecha-poliza">Fecha de póliza EC enviada al cliente</FieldLabel>
                  <Input
                    id="ed-fecha-poliza"
                    type="date"
                    value={fechaPoliza}
                    // Póliza gana min=Aprobado y max=Facturado (punto 18): el
                    // rango legal es Aprobado ≤ Póliza ≤ Facturado.
                    min={fechaAprobado || undefined}
                    max={fechaFacturado || undefined}
                    disabled={!polizaAplica}
                    onChange={e => setFechaPoliza(e.target.value)}
                    className="h-8 w-[150px] flex-none text-xs"
                  />
                </Field>
                {polizaAplica && esFechaFutura(fechaPoliza) && <FieldDescription className="text-warning">La fecha es futura</FieldDescription>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Field orientation="horizontal" className="rounded-[9px] border border-border-subtle bg-surface-page px-2.5 py-1">
                  <Checkbox defaultChecked disabled aria-label="Facturado — hito fijo" />
                  <FieldContent>
                    <FieldTitle>
                      Facturado <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">· fijo</span>
                    </FieldTitle>
                  </FieldContent>
                  {inputFechaFacturado}
                </Field>
                {esFechaFutura(fechaFacturado) && <FieldDescription className="pl-2.5 text-warning">La fecha es futura</FieldDescription>}
              </div>
            </FieldGroup>
          </FieldSet>

          {/* Nota (Juan, validación v0.12.0, 2026-09-15: «la nota es del
              acto, no del dato»): antes "Corregir hitos" no tenía forma de
              dejar nota — viaja al PUT como `motivo` y se adjunta como
              rdt_eventos.nota de los ecos de corrección/deshacer de hitos
              que este guardado genere. */}
          <Field className="mt-1">
            <FieldLabel htmlFor="ed-nota" className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Nota (opcional)
            </FieldLabel>
            <Input
              id="ed-nota"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Queda en el hilo con esta corrección"
              className="h-8 text-xs"
            />
          </Field>

          <FieldDescription className="mt-1.5">
            El estado sigue siendo derivado (del salto/monto y de los hitos con fecha). El salto/cálculo NO se edita acá: se registra como paso
            en Actividad.
          </FieldDescription>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle px-[18px] py-3">
        <span className="mr-auto text-[11px] text-text-tertiary">{isNew ? 'Los campos con * son obligatorios' : ''}</span>
        <Button type="button" variant="outline" size="sm" onClick={onCancelar} disabled={guardando}>
          Cancelar
        </Button>
        <Button type="button" size="sm" onClick={handleGuardar} disabled={guardando || (isNew && !nro.trim())}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}
